// `slide-sunset`: "> Show a graphic of B++ and Sunset." (docs/PRESENTATION.md, "Wrapping up") — a
// graphic placeholder pairing the two languages, no production art asset available at this stage
// (deferred per DESIGN.md §15 art direction).
//
// Set at the deck's own display scale rather than as two diagram nodes: this is a slide, not a
// diagram, and the two names are the whole content of it.
//
// **Two reveals since 2026-08-11** (user story 10): a **Waggle** box under B++, then an **Octopus**
// box under Sunset — the payoff lands one beat at a time rather than all at once. Each name is
// styled *subordinate* to the language above it and inherits that card's own hot/cold treatment, so
// it reads as belonging to the card rather than as a third and fourth card. Both slots are reserved
// from step 0. The tagline moved **above** the cards: it frames the pair, and under them it competed
// with the two names arriving.

import type { ReactElement, ReactNode } from 'react';
import { SlideFrame } from './SlideFrame';
import { SLIDE_SCALE } from './scale';
import { SUNSET_MAX_REVEAL, SUNSET_TAGLINE } from './content';
import './slides.css';

/** Tall enough for one `Product` badge — the reservation under each card.
 *
 * **Derived rather than eyeballed since 2026-08-13.** It was 150, and a badge is ~280 tall: the
 * revealed pair overflowed its own reserved slot by half its height, so the group the frame was
 * centring was shorter than the group on screen and the whole thing sat low on the slide. Reserving
 * what actually lands here is what centres it. The card term is its *content* height (title + role +
 * padding ≈ 238 at 1920), not its 220 `minHeight`, because the content is what wins. */
const BADGE_ARROW_HEIGHT = SLIDE_SCALE.node;
const BADGE_CARD_HEIGHT = 238;
const BADGE_SLOT_PAD_TOP = 16;
const BADGE_SLOT_HEIGHT = BADGE_ARROW_HEIGHT + 8 + BADGE_CARD_HEIGHT + BADGE_SLOT_PAD_TOP;

function LanguageCard({ name, role, hot }: { name: string; role: string; hot: boolean }): ReactElement {
  return (
    <div
      data-testid={`sunset-card-${name.toLowerCase().replaceAll('+', 'p')}`}
      className={hot ? 'deck-hot-surface' : undefined}
      style={{
        width: 460,
        minHeight: 220,
        padding: hot ? '48px 38px': '44px 32px',
        background: hot ? undefined : 'var(--deck-card)',
        border: hot ? undefined : '3px solid var(--deck-card-border)',
        borderRadius: 'var(--r-lg)',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          fontFamily: 'var(--font-display)',
          fontWeight: 600,
          fontSize: SLIDE_SCALE.title,
          color: hot ? 'var(--deck-card)' : 'var(--deck-ink)',
          lineHeight: 1,
        }}
      >
        {name}
      </div>
      <div
        style={{
          fontSize: SLIDE_SCALE.body,
          fontWeight: 500,
          color: hot ? 'var(--deck-card)' : 'var(--deck-ink)',
          marginTop: 18,
        }}
      >
        {role}
      </div>
    </div>
  );
}

/** The thing built in the language above — an arrow down and a name, at caption weight against the
 * card's title. Deliberately narrower than its card so the subordination is structural. */
function Product({ name, role, hot }: { name: string; role: string; hot: boolean }): ReactElement {
  return (
    <div
      className="demo-reveal"
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}
    >
      <span
        aria-hidden
        style={{ fontSize: SLIDE_SCALE.node, lineHeight: 1, color: 'var(--deck-accent)' }}
      >
        ↓
      </span>
      <LanguageCard name={name} role={role} hot={hot} />
    </div>
  );
}

function Pair({ card, badge }: { card: ReactNode; badge?: ReactNode }): ReactElement {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {card}
      <div
        data-testid="sunset-badge-slot"
        style={{
          height: BADGE_SLOT_HEIGHT,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          paddingTop: BADGE_SLOT_PAD_TOP,
        }}
      >
        {badge}
      </div>
    </div>
  );
}

export function SunsetSlide({ revealStep }: { revealStep: number }): ReactElement {
  const step = Math.max(0, Math.min(revealStep, SUNSET_MAX_REVEAL));
  return (
    <SlideFrame center>
      <div
        style={{
          fontFamily: 'var(--font-body)',
          fontWeight: 500,
          fontSize: SLIDE_SCALE.body,
          color: 'var(--deck-caption)',
          marginBottom: 16,
        }}
      >
        {SUNSET_TAGLINE}
      </div>
      <div style={{ display: 'flex', gap: 48, alignItems: 'flex-start' }}>
        {/* One badge per click, each carrying its own cue's beat id (`cues.ts`) so the rehearsal
            walk can tell the two reveals apart. */}
        <Pair
          card={<LanguageCard name="B++" role="for bees" hot={false} />}
          badge={
            step >= 1 ? (
              <div data-testid="reveal-sunset-waggle">
                <Product name="Waggle" role="for B++" hot={false} />
              </div>
            ) : undefined
          }
        />
        <Pair
          card={<LanguageCard name="Sunset" role="for engineering" hot />}
          badge={
            step >= 2 ? (
              <div data-testid="reveal-sunset-octopus">
                <Product name="Octopus" role="for Sunset" hot />
              </div>
            ) : undefined
          }
        />
      </div>
    </SlideFrame>
  );
}
